import { useEffect, useMemo, useRef, useState } from 'react'

import { TypedDocumentNode } from '@graphql-typed-document-node/core'

import { useQuery } from 'modules/core/hooks/useQuery'

type Employee = { id: number; access: { id: number } }

type EmployeesConnectionData = {
  employees?: {
    employeesConnection?: {
      totalCount?: number | null
      edges: { cursor: string; node: Employee }[]
      pageInfo?: { hasNextPage?: boolean; endCursor?: string | null }
    }
  }
}

type Variables = { after?: string | null; first?: number; companyId: number }

interface UseAllEmployeesWithAccessResult {
  employees: Employee[]
  accessIds: number[]
  totalCount: number
  loading: boolean
}

/**
 * Hook to load ALL employees with their access IDs, handling pagination automatically.
 * Used by NotInvitedModal and PendingModal to show complete employee lists.
 *
 * Note: Apollo doesn't auto-merge paginated results without cache config,
 * so we manually accumulate employees in local state.
 */
export function useAllEmployeesWithAccess<TData extends EmployeesConnectionData>(
  document: TypedDocumentNode<TData, Variables>,
  options: { skip?: boolean; variables: Variables }
): UseAllEmployeesWithAccessResult {
  const [allEmployees, setAllEmployees] = useState<Employee[]>([])
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [totalCount, setTotalCount] = useState(0)
  const fetchingRef = useRef(false)

  const {
    data,
    loading: initialLoading,
    fetchMore
  } = useQuery(document, {
    skip: options.skip,
    variables: options.variables
  })

  // Load all pages when initial data arrives
  useEffect(() => {
    // Skip if not ready or already fetching
    if (options.skip || initialLoading || !data || fetchingRef.current) return

    const connection = data.employees?.employeesConnection
    if (!connection) return

    fetchingRef.current = true
    setTotalCount(connection.totalCount ?? 0)

    const firstPageEmployees = connection.edges.map((edge) => edge.node)
    const hasNextPage = connection.pageInfo?.hasNextPage === true
    const endCursor = connection.pageInfo?.endCursor

    if (!hasNextPage) {
      setAllEmployees(firstPageEmployees)
      return
    }

    // Fetch remaining pages
    setIsLoadingMore(true)

    const fetchAllPages = async () => {
      const accumulated: Employee[] = [...firstPageEmployees]
      let cursor: string | null | undefined = endCursor
      let hasMore: boolean = hasNextPage

      while (hasMore && cursor) {
        const result = await fetchMore({
          variables: {
            ...options.variables,
            after: cursor
          }
        })
        const newConnection = result.data?.employees?.employeesConnection

        if (newConnection) {
          const newEmployees = newConnection.edges.map((edge) => edge.node)
          accumulated.push(...newEmployees)
          hasMore = newConnection.pageInfo?.hasNextPage ?? false
          cursor = newConnection.pageInfo?.endCursor ?? null
        } else {
          hasMore = false
        }
      }

      setAllEmployees(accumulated)
      setIsLoadingMore(false)
    }

    fetchAllPages()
  }, [data, initialLoading, options.skip, fetchMore, options.variables])

  // Reset when modal closes
  useEffect(() => {
    if (options.skip) {
      fetchingRef.current = false
      setAllEmployees([])
      setTotalCount(0)
    }
  }, [options.skip])

  const accessIds = useMemo(() => allEmployees.map((e) => e.access.id), [allEmployees])

  // Data arrived from Apollo but the effect hasn't processed it into allEmployees yet.
  // Without this, the selector briefly renders with ids: [] during the gap between
  // useQuery completing and the useEffect running.
  const pendingProcessing =
    !options.skip &&
    !initialLoading &&
    allEmployees.length === 0 &&
    (data?.employees?.employeesConnection?.totalCount ?? 0) > 0

  return {
    employees: allEmployees,
    accessIds,
    totalCount,
    loading: initialLoading || isLoadingMore || pendingProcessing
  }
}
